import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import { toast } from "sonner";
import { products } from "../data/products";
import type { Product } from "../types/product";
interface CartLine {
  slug: string;
  qty: number;
}
interface CartItem extends CartLine {
  product: Product;
}
interface CartContextValue {
  items: CartItem[];
  count: number;
  subtotal: number;
  isOpen: boolean;
  openCart: () => void;
  closeCart: () => void;
  addItem: (slug: string, qty?: number) => void;
  updateQty: (slug: string, qty: number) => void;
  removeItem: (slug: string) => void;
}
const CartContext = createContext<CartContextValue | null>(null);
export function CartProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [lines, setLines] = useState<CartLine[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const addItem = useCallback((slug: string, qty = 1) => {
    setLines(prev => {
      const existing = prev.find(l => l.slug === slug);
      if (existing) return prev.map(l => l.slug === slug ? {
        ...l,
        qty: l.qty + qty
      } : l);
      return [...prev, {
        slug,
        qty
      }];
    });
    const product = products.find(p => p.slug === slug);
    if (product) {
      toast(product.name, {
        description: qty > 1 ? `${qty} added to your cart` : "Added to your cart",
        action: {
          label: "View cart",
          onClick: () => setIsOpen(true)
        }
      });
    }
  }, []);
  const updateQty = useCallback((slug: string, qty: number) => {
    setLines(prev => qty <= 0 ? prev.filter(l => l.slug !== slug) : prev.map(l => l.slug === slug ? {
      ...l,
      qty
    } : l));
  }, []);
  const removeItem = useCallback((slug: string) => {
    setLines(prev => prev.filter(l => l.slug !== slug));
  }, []);
  const value = useMemo<CartContextValue>(() => {
    const items = lines.map(l => ({
      ...l,
      product: products.find(p => p.slug === l.slug)
    })).filter((i): i is CartItem => Boolean(i.product));
    return {
      items,
      count: items.reduce((sum, i) => sum + i.qty, 0),
      subtotal: items.reduce((sum, i) => sum + i.qty * i.product.price, 0),
      isOpen,
      openCart: () => setIsOpen(true),
      closeCart: () => setIsOpen(false),
      addItem,
      updateQty,
      removeItem
    };
  }, [lines, isOpen, addItem, updateQty, removeItem]);
  return <CartContext.Provider value={value} data-id="element-378" data-mp="3occduuhenl93">{children}</CartContext.Provider>;
}
export function useCart(): CartContextValue {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
}